import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@contexts/AuthContext";
import { ToastProvider } from "./components/ui/ToastContainer";
import { DashboardLayout } from "./layouts/DashboardLayout";

import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// New UI Pages
import DashboardPage from "./pages/DashboardPage";
import CoursesPage from "./pages/CoursesPage";
import AddEditCoursePage from "./pages/AddEditCoursePage";
import CourseStructurePage from "./pages/CourseStructurePage";
import AddEditModulePage from "./pages/AddEditModulePage";
import AddEditLessonPage from "./pages/AddEditLessonPage";
import ExamsPage from "./pages/ExamsPage";
import AddEditExamPage from "./pages/AddEditExamPage";
import StudentsPage from "./pages/StudentsPage";
import StudentDetailsPage from "./pages/StudentDetailsPage";
import InstructorsPage from "./pages/InstructorsPage";
import AddEditInstructorPage from "./pages/AddEditInstructorPage";
import InstructorDetailsPage from "./pages/InstructorDetailsPage";
import EnrollmentsPage from "./pages/EnrollmentsPage";
import CouponsPage from "./pages/CouponsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import PagesPage from "./pages/PagesPage";
import AdminsPage from "./pages/AdminsPage";
import TradesPage from "./pages/TradesPage";
import PackagesPage from "./pages/PackagesPage";
import AuthenticationPage from "./pages/AuthenticationPage";
import PaymentsPage from "./pages/PaymentsPage";
import OrdersPage from "./pages/OrdersPage";
import SliderManagementPage from "./pages/SliderManagementPage";
import SocialLinksPage from "./pages/SocialLinksPage";
import ReviewsPage from "./pages/ReviewsPage";
import DiscussionsPage from "./pages/DiscussionsPage";
import BlogsPage from "./pages/BlogsPage";
import AddEditBlogPage from "./pages/AddEditBlogPage";
import EmailCampaignsPage from "./pages/EmailCampaignsPage";
import AddEditCampaignPage from "./pages/AddEditCampaignPage";
import CampaignDetailsPage from "./pages/CampaignDetailsPage";
import GlobalChangesPage from "./pages/GlobalChangesPage";

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Login Route */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Protected Dashboard Routes - New UI */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses"
              element={
                <ProtectedRoute permission="courses:read">
                  <CoursesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/packages"
              element={
                <ProtectedRoute permission="packages:read">
                  <PackagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trades"
              element={
                <ProtectedRoute permission="trades:read">
                  <TradesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/add"
              element={
                <ProtectedRoute permission="courses:create">
                  <AddEditCoursePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/edit/:id"
              element={
                <ProtectedRoute permission="courses:update">
                  <AddEditCoursePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/:id/structure"
              element={
                <ProtectedRoute permission="courses:manage-content">
                  <CourseStructurePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/:id/modules/add"
              element={
                <ProtectedRoute permission="courses:manage-content">
                  <AddEditModulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/:id/modules/:moduleId/edit"
              element={
                <ProtectedRoute permission="courses:manage-content">
                  <AddEditModulePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/:id/modules/:moduleId/lessons/add"
              element={
                <ProtectedRoute permission="courses:manage-content">
                  <AddEditLessonPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/:id/modules/:moduleId/lessons/:lessonId"
              element={
                <ProtectedRoute permission="courses:manage-content">
                  <AddEditLessonPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/exams"
              element={
                <ProtectedRoute permission="quizzes:read">
                  <ExamsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/exams/new"
              element={
                <ProtectedRoute permission="quizzes:create">
                  <AddEditExamPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/exams/:id/edit"
              element={
                <ProtectedRoute permission="quizzes:update">
                  <AddEditExamPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/students"
              element={
                <ProtectedRoute permission="users:read">
                  <StudentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/students/:id"
              element={
                <ProtectedRoute permission="users:read">
                  <StudentDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructors"
              element={
                <ProtectedRoute permission="instructors:read">
                  <InstructorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructors/add"
              element={
                <ProtectedRoute permission="instructors:create">
                  <AddEditInstructorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructors/edit/:id"
              element={
                <ProtectedRoute permission="instructors:update">
                  <AddEditInstructorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructors/:id"
              element={
                <ProtectedRoute permission="instructors:read">
                  <InstructorDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enrollments"
              element={
                <ProtectedRoute permission="enrollments:read">
                  <EnrollmentsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/coupons"
              element={
                <ProtectedRoute permission="coupons:read">
                  <CouponsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute permission="payments:read">
                  <PaymentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute permission="orders:read">
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute permission="analytics:read">
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pages"
              element={
                <ProtectedRoute permission="pages:read">
                  <PagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admins"
              element={
                <ProtectedRoute permission="admin:read">
                  <AdminsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/authentication"
              element={
                <ProtectedRoute permission="settings:update">
                  <AuthenticationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/slider"
              element={
                <ProtectedRoute permission="settings:update">
                  <SliderManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/social-links"
              element={
                <ProtectedRoute permission="settings:update">
                  <SocialLinksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/global-changes"
              element={
                <ProtectedRoute permission="globalChanges:read">
                  <GlobalChangesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reviews"
              element={
                <ProtectedRoute permission="courses:read">
                  <ReviewsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/discussions"
              element={
                <ProtectedRoute permission="discussions:read">
                  <DiscussionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blogs"
              element={
                <ProtectedRoute permission="pages:read">
                  <BlogsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blogs/add"
              element={
                <ProtectedRoute permission="pages:read">
                  <AddEditBlogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blogs/edit/:id"
              element={
                <ProtectedRoute permission="pages:read">
                  <AddEditBlogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-campaigns"
              element={
                <ProtectedRoute permission="campaigns:read">
                  <EmailCampaignsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-campaigns/create"
              element={
                <ProtectedRoute permission="campaigns:create">
                  <AddEditCampaignPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-campaigns/edit/:id"
              element={
                <ProtectedRoute permission="campaigns:update">
                  <AddEditCampaignPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-campaigns/:id"
              element={
                <ProtectedRoute permission="campaigns:read">
                  <CampaignDetailsPage />
                </ProtectedRoute>
              }
            />

            {/* Redirect unknown routes to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
